/*
 * Copyright 2025 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.settings.sections

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.selection.selectable
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import studio.oddpods.cauce.core.model.display.DateFormat
import studio.oddpods.cauce.core.model.display.DisplayPreferences
import studio.oddpods.cauce.feature.settings.generated.resources.Res
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_date_format
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_date_format_dmy_dash
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_date_format_dmy_slash
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_date_format_ymd_dash
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_display_title
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_show_decimals
import studio.oddpods.cauce.feature.settings.generated.resources.feature_settings_thousands_separator

@Composable
internal fun DisplayPreferencesSection(
    state: DisplayPreferences,
    onChange: (DisplayPreferences) -> Unit,
    modifier: Modifier = Modifier,
) {
    OutlinedCard(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(8.dp),
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text(
                text = stringResource(Res.string.feature_settings_display_title),
                style = MaterialTheme.typography.titleMedium,
            )

            SwitchRow(
                label = stringResource(Res.string.feature_settings_show_decimals),
                checked = state.showDecimals,
                onCheckedChange = { onChange(state.copy(showDecimals = it)) },
            )
            SwitchRow(
                label = stringResource(Res.string.feature_settings_thousands_separator),
                checked = state.useThousandsSeparator,
                onCheckedChange = { onChange(state.copy(useThousandsSeparator = it)) },
            )

            Text(
                text = stringResource(Res.string.feature_settings_date_format),
                style = MaterialTheme.typography.labelLarge,
            )
            Column {
                DateFormat.entries.forEach { fmt ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .selectable(
                                selected = state.dateFormat == fmt,
                                onClick = { onChange(state.copy(dateFormat = fmt)) },
                            )
                            .padding(vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        RadioButton(
                            selected = state.dateFormat == fmt,
                            onClick = null,
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(stringResource(fmt.labelStringResource()))
                    }
                }
            }
        }
    }
}

@Composable
private fun SwitchRow(
    label: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(text = label, modifier = Modifier.weight(1f))
        Switch(checked = checked, onCheckedChange = onCheckedChange)
    }
}

private fun DateFormat.labelStringResource(): StringResource = when (this) {
    DateFormat.DMY_SLASH -> Res.string.feature_settings_date_format_dmy_slash
    DateFormat.DMY_DASH -> Res.string.feature_settings_date_format_dmy_dash
    DateFormat.YMD_DASH -> Res.string.feature_settings_date_format_ymd_dash
}
